import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Search, ChevronDown, ArrowLeft, Edit, Mail, Phone, Calendar, MessageSquare, FileText, MoreHorizontal } from 'lucide-react';
const HubSpotContactDetail: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const alertId = queryParams.get('alertId');
  const [activeTab, setActiveTab] = useState('overview');
  // Mock contacts data
  const contactsData = [{
    id: 1,
    name: 'Chensterman Chen',
    email: 'chen@swipesafe.com',
    phone: '--',
    company: 'SwipeSafe',
    lastActivity: 'Apr 19, 2025 12:39 AM',
    owner: 'No owner',
    createDate: '04/19/2025 12:39 AM PDT',
    lifecycleStage: 'Opportunity',
    lastActivityDate: '04/19/2025 12:39 AM PDT',
    domain: '--',
    leadStatus: '--',
    favoriteContent: '--',
    preferredChannels: '--'
  }, {
    id: 2,
    name: 'Hovan Ngo',
    email: 'hovan@leasa.com',
    phone: '--',
    company: 'Leasa',
    lastActivity: 'Apr 19, 2025 12:39 AM',
    owner: 'No owner',
    createDate: '04/19/2025 12:39 AM PDT',
    lifecycleStage: 'Opportunity',
    lastActivityDate: '04/19/2025 12:39 AM PDT',
    domain: '--',
    leadStatus: '--',
    favoriteContent: '--',
    preferredChannels: '--'
  }, {
    id: 3,
    name: 'Albert Feng-En Tsai',
    email: 'albert@askg.com',
    phone: '--',
    company: 'Ask G',
    lastActivity: 'Apr 19, 2025 12:39 AM',
    owner: 'No owner',
    createDate: '04/19/2025 12:39 AM PDT',
    lifecycleStage: 'Opportunity',
    lastActivityDate: '04/19/2025 12:39 AM PDT',
    domain: '--',
    leadStatus: '--',
    favoriteContent: '--',
    preferredChannels: '--'
  }, {
    id: 4,
    name: 'Ethan Bidna',
    email: 'ethan@askg.com',
    phone: '--',
    company: 'Ask G',
    lastActivity: 'Apr 19, 2025 12:39 AM',
    owner: 'No owner',
    createDate: '04/19/2025 12:39 AM PDT',
    lifecycleStage: 'Opportunity',
    lastActivityDate: '04/19/2025 12:39 AM PDT',
    domain: '--',
    leadStatus: '--',
    favoriteContent: '--',
    preferredChannels: '--'
  }, {
    id: 5,
    name: 'Jimmy Mayer',
    email: 'jimmy@loreboard.com',
    phone: '--',
    company: 'LoreBoard',
    lastActivity: 'Apr 19, 2025 12:39 AM',
    owner: 'No owner',
    createDate: '04/19/2025 12:39 AM PDT',
    lifecycleStage: 'Opportunity',
    lastActivityDate: '04/19/2025 12:39 AM PDT',
    domain: '--',
    leadStatus: '--',
    favoriteContent: '--',
    preferredChannels: '--'
  }];
  // Mock alerts data
  const alertsData = [{
    id: 1,
    name: 'Potential Analytics Upsell (High Manual Reporting)',
    description: 'Flags accounts doing heavy manual analysis, suggesting they\'re ready for the "Advanced Analytics" package.',
    category: 'Upsell & Expansion',
    matches: 5,
    lastRun: '2 hours ago',
    status: 'Active'
  }, {
    id: 2,
    name: 'Potential Brand Portal Upsell (Brand Filter Usage)',
    description: 'Identifies accounts providing reports to external partners, signaling a need for "Brand Portals".',
    category: 'Upsell & Expansion',
    matches: 8,
    lastRun: '1 day ago',
    status: 'Active'
  }, {
    id: 3,
    name: 'User Overage - License Limit Exceeded',
    description: 'A direct expansion signal that an account has added more users than their subscription allows and needs to be billed for the overage.',
    category: 'Upsell & Expansion',
    matches: 3,
    lastRun: '12 hours ago',
    status: 'Active'
  }, {
    id: 4,
    name: 'Manufacturer Not Using Assemblies',
    description: 'A critical alert for a key persona not using a sticky feature, indicating a major onboarding failure or churn risk.',
    category: 'Churn & Onboarding Risk',
    matches: 7,
    lastRun: '6 hours ago',
    status: 'Active'
  }, {
    id: 5,
    name: 'High-Value Account is Underusing the Platform',
    description: 'Flags high-MRR customers who are dangerously disengaged from the product, requiring immediate proactive outreach to prevent churn.',
    category: 'Churn & Onboarding Risk',
    matches: 2,
    lastRun: '1 day ago',
    status: 'Active'
  }, {
    id: 6,
    name: 'Distributor with Unusually Low Sales Volume',
    description: 'Identifies a distributor whose sales volume is below the expected baseline for their persona, suggesting a potential business or adoption problem.',
    category: 'Churn & Onboarding Risk',
    matches: 4,
    lastRun: '8 hours ago',
    status: 'Active'
  }, {
    id: 7,
    name: 'Small Customer Using E-commerce Menu Exclusively',
    description: 'A strategic alert for the product team that identifies customers who are essentially buying the entire platform for one feature, signaling a potential to modularize that feature into a new product.',
    category: 'Product & Strategy',
    matches: 6,
    lastRun: '3 hours ago',
    status: 'Active'
  }];
  // Find the current contact
  const currentContact = contactsData.find(contact => contact.id === parseInt(id || '1')) || contactsData[0];
  // Find the current alert
  const currentAlert = alertsData.find(alert => alert.id === parseInt(alertId || '1'));
  // Generate mock alert statuses for this contact
  const contactAlertStatuses = alertsData.map(alert => ({
    id: alert.id,
    name: alert.name,
    status: Math.random() > 0.6 ? 'Triggered' : 'Not Triggered',
    lastTriggered: Math.random() > 0.6 ? 'Apr 18, 2025' : '--',
    category: alert.category
  }));
  return <div className="bg-white min-h-screen" data-id="element-1046">
      {/* HubSpot-like header */}
      <div className="border-b border-gray-200 bg-[#33475b] text-white py-2 px-4 flex items-center justify-between" data-id="element-1047">
        <div className="flex items-center" data-id="element-1048">
          <div className="mr-4" data-id="element-1049">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" data-id="element-1050">
              <path d="M22.447 10.105h-6.225v-1.293h6.225v1.293zm-6.225 4.278h6.225v-1.293h-6.225v1.293zm0 4.278h6.225v-1.293h-6.225v1.293zm-7.437-2.985a3.026 3.026 0 0 1-3.022-3.022 3.026 3.026 0 0 1 3.022-3.022 3.026 3.026 0 0 1 3.022 3.022 3.026 3.026 0 0 1-3.022 3.022m0-8.557a5.542 5.542 0 0 0-5.535 5.535 5.542 5.542 0 0 0 5.535 5.535 5.542 5.542 0 0 0 5.535-5.535 5.542 5.542 0 0 0-5.535-5.535m-5.535-3.022h4.242V1.855H3.25v4.242h4.242z" data-id="element-1051" />
            </svg>
          </div>
          <div className="relative" data-id="element-1052">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-1053" />
            <input type="text" placeholder="Search HubSpot" className="bg-[#496b8a] text-white placeholder-gray-300 rounded pl-10 pr-4 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1054" />
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 text-xs" data-id="element-1055">
              Ctrl K
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-4" data-id="element-1056">
          <button className="text-white" data-id="element-1057">
            <span className="sr-only" data-id="element-1058">Notifications</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1059">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" data-id="element-1060"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0" data-id="element-1061"></path>
            </svg>
          </button>
          <div className="flex items-center" data-id="element-1062">
            <span className="text-sm font-medium mr-2" data-id="element-1063">Daemo AI</span>
            <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center" data-id="element-1064">
              DA
            </div>
          </div>
        </div>
      </div>
      {/* Back link and contact header */}
      <div className="p-4 border-b border-gray-200" data-id="element-1065">
        <div className="mb-2" data-id="element-1066">
          <button onClick={() => navigate(`/hubspot/lists/${alertId}`)} className="text-blue-600 hover:text-blue-800 text-sm flex items-center" data-id="element-1067">
            <ArrowLeft size={14} className="mr-1" data-id="element-1068" /> Contacts
          </button>
        </div>
        <div className="flex items-start" data-id="element-1069">
          <div className="flex-shrink-0 mr-4" data-id="element-1070">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 text-2xl" data-id="element-1071">
              {currentContact.name.charAt(0)}
            </div>
          </div>
          <div className="flex-1" data-id="element-1072">
            <div className="flex justify-between items-start" data-id="element-1073">
              <div data-id="element-1074">
                <h1 className="text-xl font-medium text-gray-800" data-id="element-1075">
                  {currentContact.name}
                </h1>
                <p className="text-sm text-gray-600" data-id="element-1076">
                  {currentContact.company}
                </p>
              </div>
              <div className="flex space-x-2" data-id="element-1077">
                <button className="bg-[#ff7a59] hover:bg-[#ff8f73] text-white rounded px-3 py-1.5 text-sm flex items-center" data-id="element-1078">
                  Actions <ChevronDown size={14} className="ml-1" data-id="element-1079" />
                </button>
                <button className="border border-gray-300 rounded px-3 py-1.5 text-sm flex items-center text-gray-700 hover:bg-gray-50" data-id="element-1080">
                  <svg className="mr-1" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1081">
                    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" data-id="element-1082"></polyline>
                    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" data-id="element-1083"></path>
                  </svg>
                  Breeze record summary
                </button>
              </div>
            </div>
            <div className="flex mt-3 space-x-2" data-id="element-1084">
              <button className="p-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50" data-id="element-1085">
                <Edit size={16} data-id="element-1086" />
              </button>
              <button className="p-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50" data-id="element-1087">
                <Mail size={16} data-id="element-1088" />
              </button>
              <button className="p-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50" data-id="element-1089">
                <Phone size={16} data-id="element-1090" />
              </button>
              <button className="p-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50" data-id="element-1091">
                <MessageSquare size={16} data-id="element-1092" />
              </button>
              <button className="p-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50" data-id="element-1093">
                <Calendar size={16} data-id="element-1094" />
              </button>
              <button className="p-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50" data-id="element-1095">
                <FileText size={16} data-id="element-1096" />
              </button>
              <button className="p-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50" data-id="element-1097">
                <MoreHorizontal size={16} data-id="element-1098" />
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200 flex" data-id="element-1099">
        <button className={`py-3 px-6 text-sm font-medium ${activeTab === 'overview' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`} onClick={() => setActiveTab('overview')} data-id="element-1100">
          Overview
        </button>
        <button className={`py-3 px-6 text-sm font-medium ${activeTab === 'activities' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`} onClick={() => setActiveTab('activities')} data-id="element-1101">
          Activities
        </button>
        <button className={`py-3 px-6 text-sm font-medium ${activeTab === 'intelligence' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`} onClick={() => setActiveTab('intelligence')} data-id="element-1102">
          Intelligence
        </button>
        <div className="flex-1 flex justify-end" data-id="element-1103">
          <button className="py-3 px-6 text-sm font-medium text-blue-600 flex items-center" data-id="element-1104">
            <svg className="mr-1" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1105">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-1106"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-1107"></path>
            </svg>
            Customize
          </button>
        </div>
      </div>
      {/* Main content */}
      <div className="flex p-4" data-id="element-1108">
        {/* Left column */}
        <div className="w-3/4 pr-4" data-id="element-1109">
          {/* Data highlights */}
          <div className="mb-6" data-id="element-1110">
            <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1111">
              Data highlights
            </h2>
            <div className="grid grid-cols-3 gap-4" data-id="element-1112">
              <div className="border border-gray-200 rounded-md p-4" data-id="element-1113">
                <h3 className="text-xs text-gray-500 uppercase mb-1" data-id="element-1114">
                  Create Date
                </h3>
                <p className="text-sm text-gray-900" data-id="element-1115">
                  {currentContact.createDate}
                </p>
              </div>
              <div className="border border-gray-200 rounded-md p-4" data-id="element-1116">
                <h3 className="text-xs text-gray-500 uppercase mb-1" data-id="element-1117">
                  Lifecycle Stage
                </h3>
                <p className="text-sm text-gray-900" data-id="element-1118">
                  {currentContact.lifecycleStage}
                </p>
              </div>
              <div className="border border-gray-200 rounded-md p-4" data-id="element-1119">
                <h3 className="text-xs text-gray-500 uppercase mb-1" data-id="element-1120">
                  Last Activity Date
                </h3>
                <p className="text-sm text-gray-900" data-id="element-1121">
                  {currentContact.lastActivityDate}
                </p>
              </div>
            </div>
          </div>
          {/* Daemo Alerts section - Added to show alert-specific fields */}
          <div className="mb-6" data-id="element-1122">
            <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1123">
              Daemo Alerts
            </h2>
            <div className="border border-gray-200 rounded-md" data-id="element-1124">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1125">
                <thead className="bg-gray-50" data-id="element-1126">
                  <tr data-id="element-1127">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1128">
                      Alert Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1129">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1130">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1131">
                      Last Triggered
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1132">
                  {contactAlertStatuses.map(alert => <tr key={alert.id} className={`${currentAlert && currentAlert.id === alert.id ? 'bg-blue-50' : ''}`} data-id="element-1133">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1134">
                        <div className="text-sm text-gray-900" data-id="element-1135">
                          {alert.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1136">
                        <div className="text-sm text-gray-600" data-id="element-1137">
                          {alert.category}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1138">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${alert.status === 'Triggered' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`} data-id="element-1139">
                          {alert.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1140">
                        {alert.lastTriggered}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
          {/* Recent activities */}
          <div className="mb-6" data-id="element-1141">
            <div className="flex justify-between items-center mb-4" data-id="element-1142">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-1143">
                Recent activities
              </h2>
              <button className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-1144">
                Collapse all
              </button>
            </div>
            <div className="border border-gray-200 rounded-md p-4" data-id="element-1145">
              <div className="flex items-center justify-between mb-4" data-id="element-1146">
                <div className="flex items-center text-blue-600 text-sm font-medium" data-id="element-1147">
                  <span data-id="element-1148">(5) Activity</span>
                  <button className="ml-2" data-id="element-1149">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1150">
                      <line x1="18" y1="6" x2="6" y2="18" data-id="element-1151"></line>
                      <line x1="6" y1="6" x2="18" y2="18" data-id="element-1152"></line>
                    </svg>
                  </button>
                </div>
                <div className="text-sm text-gray-600" data-id="element-1153">All time so far</div>
              </div>
              <div className="border-t border-gray-200 pt-4" data-id="element-1154">
                <div className="flex items-start mb-4" data-id="element-1155">
                  <div className="flex-shrink-0 mr-3" data-id="element-1156">
                    <FileText size={16} className="text-blue-600" data-id="element-1157" />
                  </div>
                  <div className="flex-1" data-id="element-1158">
                    <div className="flex items-center justify-between" data-id="element-1159">
                      <div className="flex items-center" data-id="element-1160">
                        <span className="text-sm font-medium text-blue-600 mr-2" data-id="element-1161">
                          Note
                        </span>
                        <span className="text-sm text-gray-600" data-id="element-1162">
                          created via
                        </span>
                        <span className="text-sm font-medium text-blue-600 ml-1" data-id="element-1163">
                          Import
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-1164">
                        Apr 19, 2025 at 12:39 AM PDT
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-2" data-id="element-1165">
                      Automatic background check for dating apps.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Contacts section */}
          <div className="mb-6" data-id="element-1166">
            <div className="flex justify-between items-center mb-4" data-id="element-1167">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-1168">Contacts</h2>
              <button className="border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1169">
                Add
              </button>
            </div>
            <div className="border border-gray-200 rounded-md p-4 text-center" data-id="element-1170">
              <p className="text-sm text-gray-600" data-id="element-1171">
                No associated objects of this type exist or you don't have
                permission to view them.
              </p>
            </div>
          </div>
          {/* Companies section */}
          <div className="mb-6" data-id="element-1172">
            <div className="flex justify-between items-center mb-4" data-id="element-1173">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-1174">Companies</h2>
              <button className="border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1175">
                Add
              </button>
            </div>
            <div className="border border-gray-200 rounded-md" data-id="element-1176">
              <div className="p-4 flex justify-between items-center border-b border-gray-200" data-id="element-1177">
                <div className="flex space-x-2" data-id="element-1178">
                  <button className="border border-gray-300 rounded px-3 py-1 text-xs text-gray-700 hover:bg-gray-50" data-id="element-1179">
                    Company owner
                  </button>
                  <button className="border border-gray-300 rounded px-3 py-1 text-xs text-gray-700 hover:bg-gray-50" data-id="element-1180">
                    Lead status
                  </button>
                  <button className="border border-gray-300 rounded px-3 py-1 text-xs text-gray-700 hover:bg-gray-50" data-id="element-1181">
                    Last activity date
                  </button>
                  <button className="border border-gray-300 rounded px-3 py-1 text-xs text-gray-700 hover:bg-gray-50" data-id="element-1182">
                    Create date
                  </button>
                </div>
                <div className="flex items-center" data-id="element-1183">
                  <Search size={14} className="text-gray-400 mr-2" data-id="element-1184" />
                  <input type="text" placeholder="Search" className="border-none focus:outline-none text-sm" data-id="element-1185" />
                </div>
              </div>
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1186">
                <thead className="bg-gray-50" data-id="element-1187">
                  <tr data-id="element-1188">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1189">
                      Company Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1190">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1191">
                      Company Domain Name
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1192">
                  <tr data-id="element-1193">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1194">
                      <div className="flex items-center" data-id="element-1195">
                        <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-600" data-id="element-1196">
                          {currentContact.company.charAt(0)}
                        </div>
                        <div className="ml-4" data-id="element-1197">
                          <div className="text-sm font-medium text-blue-600 hover:underline" data-id="element-1198">
                            {currentContact.company}
                          </div>
                          <div className="text-xs px-2 py-0.5 bg-gray-100 rounded-sm inline-block" data-id="element-1199">
                            Primary
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1200">
                      <div className="text-sm text-gray-900" data-id="element-1201">--</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1202">
                      <div className="text-sm text-gray-900" data-id="element-1203">--</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Right column */}
        <div className="w-1/4" data-id="element-1204">
          {/* Contact summary */}
          <div className="border border-gray-200 rounded-md mb-4" data-id="element-1205">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1206">
              <h2 className="text-sm font-medium text-gray-800" data-id="element-1207">
                Contact summary
              </h2>
              <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded" data-id="element-1208">
                AI
              </span>
            </div>
            <div className="p-4" data-id="element-1209">
              <div className="flex justify-between items-center text-xs text-gray-600 mb-2" data-id="element-1210">
                <span data-id="element-1211">Generated Aug 18, 2025</span>
                <button data-id="element-1212">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1213">
                    <path d="M21 2v6h-6" data-id="element-1214"></path>
                    <path d="M3 15v6h6" data-id="element-1215"></path>
                    <path d="M21 8l-9 9-9-9" data-id="element-1216"></path>
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-700 mb-4" data-id="element-1217">
                The most recent activity for this contact includes a note from
                April 19, 2025, regarding an automatic background check for
                dating apps.
              </p>
              <div className="flex space-x-2" data-id="element-1218">
                <button className="p-1 border border-gray-200 rounded" data-id="element-1219">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1220">
                    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" data-id="element-1221"></path>
                  </svg>
                </button>
                <button className="p-1 border border-gray-200 rounded" data-id="element-1222">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1223">
                    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" data-id="element-1224"></path>
                  </svg>
                </button>
                <button className="p-1 border border-gray-200 rounded" data-id="element-1225">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1226">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" data-id="element-1227"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" data-id="element-1228"></path>
                  </svg>
                </button>
              </div>
              <button className="mt-4 w-full text-center border border-pink-500 text-pink-500 rounded-md py-1.5 text-sm hover:bg-pink-50" data-id="element-1229">
                Ask a question
              </button>
            </div>
          </div>
          {/* Companies */}
          <div className="border border-gray-200 rounded-md mb-4" data-id="element-1230">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1231">
              <h2 className="text-sm font-medium text-gray-800" data-id="element-1232">
                Companies (1)
              </h2>
              <button className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-1233">
                + Add
              </button>
            </div>
            <div className="p-4" data-id="element-1234">
              <div className="mb-2" data-id="element-1235">
                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-sm" data-id="element-1236">
                  Primary
                </span>
              </div>
              <div className="flex items-center mb-2" data-id="element-1237">
                <div className="flex-shrink-0 h-6 w-6 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 text-xs mr-2" data-id="element-1238">
                  {currentContact.company.charAt(0)}
                </div>
                <a href="#" className="text-sm text-blue-600 hover:underline" data-id="element-1239">
                  {currentContact.company}
                </a>
              </div>
              <div className="text-xs text-gray-600 mb-1" data-id="element-1240">Domain: --</div>
              <div className="text-xs text-gray-600" data-id="element-1241">Phone: --</div>
              <div className="mt-3" data-id="element-1242">
                <a href="#" className="text-xs text-blue-600 hover:underline" data-id="element-1243">
                  View associated Company
                </a>
              </div>
            </div>
          </div>
          {/* Deals */}
          <div className="border border-gray-200 rounded-md mb-4" data-id="element-1244">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1245">
              <h2 className="text-sm font-medium text-gray-800" data-id="element-1246">Deals (1)</h2>
              <button className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-1247">
                + Add
              </button>
            </div>
            <div className="p-4" data-id="element-1248">
              <div className="mb-3" data-id="element-1249">
                <a href="#" className="text-sm text-blue-600 hover:underline" data-id="element-1250">
                  {currentContact.company} - Launchyard
                </a>
              </div>
              <div className="text-xs text-gray-600 mb-1" data-id="element-1251">Amount: --</div>
              <div className="text-xs text-gray-600 mb-1" data-id="element-1252">Close date: --</div>
              <div className="text-xs text-gray-600 flex items-center mb-3" data-id="element-1253">
                Stage:{' '}
                <span className="text-blue-600 ml-1" data-id="element-1254">Inbound Lead Sourced</span>{' '}
                <ChevronDown size={12} className="ml-1 text-blue-600" data-id="element-1255" />
              </div>
              <a href="#" className="text-xs text-blue-600 hover:underline" data-id="element-1256">
                View associated Deal
              </a>
            </div>
          </div>
          {/* Tickets */}
          <div className="border border-gray-200 rounded-md mb-4" data-id="element-1257">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1258">
              <h2 className="text-sm font-medium text-gray-800" data-id="element-1259">Tickets (0)</h2>
              <button className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-1260">
                + Add
              </button>
            </div>
          </div>
          {/* About this contact */}
          <div className="border border-gray-200 rounded-md mb-4" data-id="element-1261">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1262">
              <h2 className="text-sm font-medium text-gray-800" data-id="element-1263">
                About this contact
              </h2>
              <button className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-1264">
                Actions
              </button>
            </div>
            <div className="p-4" data-id="element-1265">
              <div className="mb-3" data-id="element-1266">
                <div className="text-xs text-gray-600 mb-1" data-id="element-1267">Email</div>
                <div className="text-sm text-gray-900" data-id="element-1268">--</div>
              </div>
              <div className="mb-3" data-id="element-1269">
                <div className="text-xs text-gray-600 mb-1" data-id="element-1270">Phone number</div>
                <div className="text-sm text-gray-900" data-id="element-1271">--</div>
              </div>
              <div className="mb-3" data-id="element-1272">
                <div className="text-xs text-gray-600 mb-1" data-id="element-1273">
                  Preferred channels
                </div>
                <div className="text-sm text-gray-900" data-id="element-1274">--</div>
              </div>
              <div className="mb-3" data-id="element-1275">
                <div className="text-xs text-gray-600 mb-1" data-id="element-1276">
                  Favorite Content Topics
                </div>
                <div className="text-sm text-gray-900" data-id="element-1277">--</div>
              </div>
              <div className="mb-3" data-id="element-1278">
                <div className="text-xs text-gray-600 mb-1" data-id="element-1279">Lead status</div>
                <div className="text-sm text-gray-900" data-id="element-1280">--</div>
              </div>
              <div className="mb-3" data-id="element-1281">
                <div className="text-xs text-gray-600 mb-1" data-id="element-1282">Create date</div>
                <div className="text-sm text-gray-900" data-id="element-1283">
                  {currentContact.createDate}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default HubSpotContactDetail;